import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ThumbsUp, Bell, BellOff, Sparkles } from 'lucide-react';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';
import { ErrorBanner } from './Configuration';
import { MultiSelect, SearchBox, FilterCheckbox } from './registerFilters';
import {
  AREAS, CATEGORIES, STATUSES, OPEN_STATUSES, EDITABLE_STATUSES, PRIORITIES, STATUS_BADGE,
} from '../suggestions/SuggestionBox';

const DAY = 86400000;
const BUG = 'Bug or annoyance';
const PRIORITY_RANK = { Critical: 4, High: 3, Medium: 2, Low: 1 };
const PRIORITY_BADGE = { Critical: 'badge-red', High: 'badge-yellow', Medium: 'badge-blue', Low: 'badge-gray' };

const fmtDate = (v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const fmtDateTime = (v) => new Date(v).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const dayInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');
const deliveredOn = (s) => +new Date(s.deliveredAt || s.updatedAt);
const opt = (list) => list.map((v) => ({ value: v, label: v }));
const clip = (t, n = 90) => (t.length > n ? `${t.slice(0, n).trimEnd()}…` : t);

const SORTS = {
  newest: { label: 'Newest first', fn: (a, b) => +new Date(b.createdAt) - +new Date(a.createdAt) },
  oldest: { label: 'Oldest first', fn: (a, b) => +new Date(a.createdAt) - +new Date(b.createdAt) },
  votes: { label: 'Most votes', fn: (a, b) => b.voteCount - a.voteCount || +new Date(b.createdAt) - +new Date(a.createdAt) },
  priority: {
    label: 'Priority',
    fn: (a, b) => (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0) || b.voteCount - a.voteCount,
  },
};

/** Runs one mutation against a card, surfacing the error inline and handing the fresh row up. */
function useMutation(onChange) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (path, method, body) => {
    setBusy(true);
    setError('');
    try {
      const updated = await apiFetch(`/suggestions/${path}`, { method, ...(body ? { body: JSON.stringify(body) } : {}) });
      onChange(updated);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

function Person({ person }) {
  const name = person?.name || 'Unknown';
  return <span className="sg-person"><span className="sg-avatar" aria-hidden>{name.charAt(0).toUpperCase()}</span>{name}</span>;
}

function Thread({ s, ctx, mutate }) {
  const [body, setBody] = useState('');
  const [editing, setEditing] = useState(null); // { id, body }

  const post = async () => { if (await mutate.run(`${s.id}/replies`, 'POST', { body })) setBody(''); };
  const saveEdit = async () => { if (await mutate.run(`${s.id}/replies/${editing.id}`, 'PATCH', { body: editing.body })) setEditing(null); };

  return (
    <div className="sg-thread">
      {s.replies.map((r) => (
        <div key={r.id} className="sg-reply">
          <div className="sg-reply-head">
            <Person person={r.author} />
            <span className="sg-faint">{fmtDateTime(r.createdAt)}{r.editedAt ? ' · edited' : ''}</span>
            <span className="sg-reply-actions">
              {r.author.id === ctx.me.id && editing?.id !== r.id && (
                <button type="button" className="sg-link" onClick={() => setEditing({ id: r.id, body: r.body })}>Edit</button>
              )}
              {(r.author.id === ctx.me.id || ctx.isAdmin) && (
                <button type="button" className="sg-link" onClick={() => mutate.run(`${s.id}/replies/${r.id}`, 'DELETE')}>Delete</button>
              )}
            </span>
          </div>
          {editing?.id === r.id ? (
            <div>
              <textarea className="form-input" rows={2} maxLength={2000} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
              <div className="sg-row">
                <button type="button" className="btn btn-primary btn-sm" disabled={mutate.busy || !editing.body.trim()} onClick={saveEdit}>Save</button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="sg-reply-body">{r.body}</div>
          )}
        </div>
      ))}
      <div className="sg-compose">
        <textarea
          className="form-input"
          rows={2}
          maxLength={2000}
          placeholder="Add a reply…"
          aria-label="Add a reply"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <button type="button" className="btn btn-primary btn-sm" disabled={mutate.busy || !body.trim()} onClick={post}>Reply</button>
      </div>
    </div>
  );
}

/** Admin-only roadmap + delivery controls. Sends only the fields that changed, so a status move to
 *  Done keeps the server's auto-stamped delivery date unless the admin typed one. */
function TriagePanel({ s, ctx, mutate, onMerged }) {
  const initial = useMemo(() => ({
    status: s.status,
    priority: s.priority || '',
    targetRelease: s.targetRelease || '',
    area: s.area || '',
    deliveredAt: dayInput(s.deliveredAt),
    deliveredById: s.deliveredBy?.id || '',
    releaseNote: s.releaseNote || '',
    linkUrl: s.linkUrl || '',
  }), [s]);
  const [form, setForm] = useState(initial);
  const [mergeTarget, setMergeTarget] = useState('');
  useEffect(() => setForm(initial), [initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const changed = Object.keys(form).filter((k) => form[k] !== initial[k]);

  const save = () => {
    const payload = {};
    changed.forEach((k) => {
      payload[k] = k === 'deliveredAt' || k === 'deliveredById' ? form[k] || null : form[k];
    });
    mutate.run(s.id, 'PATCH', payload);
  };

  const merge = async (targetId) => { if (await mutate.run(`${s.id}/merge`, 'POST', { targetId })) onMerged(); };
  const dupTarget = ctx.items.find((x) => x.id === s.aiDuplicateOfId);
  const related = (s.aiRelatedIds || []).map((id) => ctx.items.find((x) => x.id === id)).filter(Boolean);
  const mergeOptions = ctx.items.filter((x) => x.id !== s.id && OPEN_STATUSES.includes(x.status));

  return (
    <div className="sg-triage">
      {(s.aiSummary || related.length > 0) && (
        <div className="sg-ai">
          <Sparkles size={14} aria-hidden /> <strong>AI triage:</strong> {s.aiSummary}
          {s.aiCategory && s.aiCategory !== s.category && (
            <button type="button" className="sg-link" onClick={() => mutate.run(s.id, 'PATCH', { category: s.aiCategory })}>Set category to “{s.aiCategory}”</button>
          )}
          {related.length > 0 && (
            <div>Related, worth planning together: {related.map((x, i) => (
              <React.Fragment key={x.id}>{i > 0 && ', '}<button type="button" className="sg-link" onClick={() => ctx.open(x.id)}>“{clip(x.text, 50)}”</button></React.Fragment>
            ))}</div>
          )}
          {dupTarget && s.status !== 'Duplicate' && (
            <div>Possible duplicate of “{clip(dupTarget.text, 70)}” <button type="button" className="sg-link" onClick={() => merge(dupTarget.id)}>Merge into it</button></div>
          )}
        </div>
      )}
      <div className="sg-grid">
        <div className="form-group"><label className="form-label">Status</label>
          <select className="form-select" value={form.status} onChange={set('status')}>
            {STATUSES.filter((x) => x !== 'Duplicate' || s.status === 'Duplicate').map((x) => <option key={x}>{x}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Priority</label>
          <select className="form-select" value={form.priority} onChange={set('priority')}>
            <option value="">—</option>{PRIORITIES.map((x) => <option key={x}>{x}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Area</label>
          <select className="form-select" value={form.area} onChange={set('area')}>
            <option value="">—</option>{AREAS.map((x) => <option key={x}>{x}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Target release</label>
          <input className="form-input" maxLength={100} placeholder="e.g. Oct 2026" value={form.targetRelease} onChange={set('targetRelease')} /></div>
        <div className="form-group"><label className="form-label">Delivered on</label>
          <input className="form-input" type="date" value={form.deliveredAt} onChange={set('deliveredAt')} /></div>
        <div className="form-group"><label className="form-label">Delivered by</label>
          <select className="form-select" value={form.deliveredById} onChange={set('deliveredById')}>
            <option value="">—</option>
            {ctx.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Link (PR, ADR, doc)</label>
          <input className="form-input" maxLength={500} placeholder="https://…" value={form.linkUrl} onChange={set('linkUrl')} /></div>
      </div>
      <div className="form-group"><label className="form-label">Release note — what shipped</label>
        <input className="form-input" maxLength={1000} value={form.releaseNote} onChange={set('releaseNote')} /></div>
      <div className="sg-row">
        <button type="button" className="btn btn-primary btn-sm" disabled={mutate.busy || !changed.length} onClick={save}>Save changes</button>
        {s.status !== 'Duplicate' && (
          <>
            <select className="form-select" style={{ maxWidth: 260 }} aria-label="Merge into" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}>
              <option value="">Mark as duplicate of…</option>
              {mergeOptions.map((x) => <option key={x.id} value={x.id}>{clip(x.text, 50)}</option>)}
            </select>
            <button type="button" className="btn btn-outline btn-sm" disabled={mutate.busy || !mergeTarget} onClick={() => merge(mergeTarget)}>Merge</button>
          </>
        )}
      </div>
    </div>
  );
}

function SuggestionCard({ s, ctx, onChange, defaultOpen = false }) {
  const mutate = useMutation(onChange);
  const [open, setOpen] = useState(defaultOpen);
  // Admins land on the detail dialog with triage already open -- that is where delivery gets recorded.
  const [triageOpen, setTriageOpen] = useState(ctx.isAdmin && defaultOpen);
  const [edit, setEdit] = useState(null); // { text, category }

  const isOwner = s.submittedBy?.id === ctx.me.id;
  const canEdit = ctx.isAdmin || (isOwner && EDITABLE_STATUSES.includes(s.status));
  const saveEdit = async () => { if (await mutate.run(s.id, 'PATCH', edit)) setEdit(null); };

  return (
    <article className="sg-card" data-status={s.status}>
      <div className="sg-main">
        <button
          type="button"
          className={`sg-vote${s.votedByMe ? ' is-on' : ''}`}
          aria-pressed={s.votedByMe}
          aria-label={`${s.votedByMe ? 'Remove your vote' : 'Vote'} (${s.voteCount})`}
          title={s.votedByMe ? 'Remove your vote' : 'Me too'}
          disabled={mutate.busy}
          onClick={() => mutate.run(`${s.id}/vote`, 'POST')}
        >
          <ThumbsUp size={15} aria-hidden /><span>{s.voteCount}</span>
        </button>
        <div className="sg-body">
          <div className="sg-badges">
            {ctx.isAdmin && s.status !== 'Duplicate' ? (
              // Always-visible status control for admins: the everyday "work it" action. Moving to
              // Done opens the triage panel so the release note and link get filled in.
              <select
                className="form-select sg-status-select"
                aria-label="Status"
                value={s.status}
                disabled={mutate.busy}
                onChange={async (e) => {
                  const next = e.target.value;
                  if (await mutate.run(s.id, 'PATCH', { status: next }) && next === 'Done') {
                    setTriageOpen(true);
                  }
                }}
              >
                {STATUSES.filter((x) => x !== 'Duplicate').map((x) => <option key={x}>{x}</option>)}
              </select>
            ) : (
              <span className={`badge ${STATUS_BADGE[s.status] || 'badge-gray'}`}>{s.status}</span>
            )}
            <span className={`badge ${s.category === BUG ? 'badge-red' : 'badge-gray'}`}>{s.category}</span>
            {s.area && <span className="badge badge-gray">{s.area}</span>}
            {s.priority && <span className={`badge ${PRIORITY_BADGE[s.priority]}`}>{s.priority}</span>}
            {s.targetRelease && <span className="badge badge-violet">Target: {s.targetRelease}</span>}
          </div>

          {edit ? (
            <div>
              <select className="form-select" style={{ maxWidth: 220, marginBottom: 6 }} aria-label="Category" value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
              <textarea className="form-input" rows={4} maxLength={4000} aria-label="Suggestion text" value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} />
              <div className="sg-row">
                <button type="button" className="btn btn-primary btn-sm" disabled={mutate.busy || edit.text.trim().length < 3} onClick={saveEdit}>Save</button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setEdit(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <p className="sg-text">{s.text}</p>
          )}

          <div className="sg-meta">
            <Person person={s.submittedBy} />
            <span>{fmtDate(s.createdAt)}</span>
            {s.editedAt && <span title={fmtDateTime(s.editedAt)}>edited</span>}
            {s.pageContext && <span title="Page the user was on">on {s.pageContext}</span>}
            {s.duplicateOf && <span>Duplicate of “{clip(s.duplicateOf.text, 50)}”</span>}
            {canEdit && !edit && <button type="button" className="sg-link" onClick={() => setEdit({ text: s.text, category: s.category })}>Edit</button>}
            {isOwner && !canEdit && <span className="sg-faint">Locked once planned</span>}
          </div>

          {s.status === 'Done' && (s.deliveredAt || s.releaseNote) && (
            <div className="sg-delivered">
              <strong>Delivered {s.deliveredAt ? fmtDate(s.deliveredAt) : ''}{s.deliveredBy ? ` by ${s.deliveredBy.name}` : ''}</strong>
              {s.releaseNote && <div>{s.releaseNote}</div>}
              {s.linkUrl && <a href={s.linkUrl} target="_blank" rel="noreferrer">{s.linkUrl}</a>}
            </div>
          )}
          {s.status !== 'Done' && s.linkUrl && <a className="sg-meta" href={s.linkUrl} target="_blank" rel="noreferrer">{s.linkUrl}</a>}

          <div className="sg-row sg-actions">
            <button type="button" className="sg-link" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
              {open ? 'Hide discussion' : `Discussion (${s.replies.length})`}
            </button>
            <button type="button" className="sg-link" onClick={() => mutate.run(`${s.id}/watch`, 'POST')} disabled={mutate.busy}>
              {s.watching ? <><BellOff size={13} aria-hidden /> Unfollow</> : <><Bell size={13} aria-hidden /> Follow</>}
            </button>
            {ctx.isAdmin && (
              <button type="button" className="sg-link" onClick={() => setTriageOpen((v) => !v)} aria-expanded={triageOpen}>
                {triageOpen ? 'Hide roadmap & delivery' : 'Roadmap & delivery'}
              </button>
            )}
          </div>

          {mutate.error && <div className="sg-error" role="alert">{mutate.error}</div>}
          {ctx.isAdmin && triageOpen && <TriagePanel s={s} ctx={ctx} mutate={mutate} onMerged={ctx.reload} />}
          {open && <Thread s={s} ctx={ctx} mutate={mutate} />}
        </div>
      </div>
    </article>
  );
}

/** Compact card for the roadmap board; click opens the full card in a dialog. */
function MiniCard({ s, onOpen }) {
  return (
    <button type="button" className="sg-mini" data-status={s.status} onClick={() => onOpen(s.id)}>
      <span className="sg-mini-text">{clip(s.text, 110)}</span>
      <span className="sg-mini-meta">
        {s.category === BUG && <span className="badge badge-red">Bug</span>}
        {s.priority && <span className={`badge ${PRIORITY_BADGE[s.priority]}`}>{s.priority}</span>}
        {s.targetRelease && <span className="sg-faint">{s.targetRelease}</span>}
        <span className="sg-faint"><ThumbsUp size={11} aria-hidden /> {s.voteCount}</span>
        {s.replies.length > 0 && <span className="sg-faint">{s.replies.length} repl{s.replies.length === 1 ? 'y' : 'ies'}</span>}
      </span>
    </button>
  );
}

const BOARD = [
  { key: 'inbox', title: 'Inbox', hint: 'New and under review', match: (s) => s.status === 'New' || s.status === 'Under review' },
  { key: 'planned', title: 'Planned', hint: 'Committed, not started', match: (s) => s.status === 'Planned' },
  { key: 'progress', title: 'In progress', hint: 'Being built', match: (s) => s.status === 'In progress' },
  { key: 'done', title: 'Delivered', hint: 'Last 90 days', match: (s) => s.status === 'Done' && Date.now() - deliveredOn(s) < 90 * DAY },
];

function Changelog({ items, onOpen }) {
  const groups = useMemo(() => {
    const done = items.filter((s) => s.status === 'Done').sort((a, b) => deliveredOn(b) - deliveredOn(a));
    const byMonth = new Map();
    done.forEach((s) => {
      const label = new Date(deliveredOn(s)).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      byMonth.set(label, [...(byMonth.get(label) || []), s]);
    });
    return [...byMonth.entries()];
  }, [items]);

  if (!groups.length) return <div className="sg-empty">Nothing delivered yet.</div>;
  return groups.map(([month, rows]) => (
    <section key={month} className="sg-month">
      <h2>{month}</h2>
      {rows.map((s) => (
        <button type="button" key={s.id} className="sg-log" onClick={() => onOpen(s.id)}>
          <span className="sg-log-date">{fmtDate(deliveredOn(s))}</span>
          <span className="sg-log-main">
            <strong>{s.releaseNote || clip(s.text, 120)}</strong>
            <span className="sg-faint">
              Suggested by {s.submittedBy?.name || 'Unknown'}{s.deliveredBy ? ` · delivered by ${s.deliveredBy.name}` : ''}
              {s.category === BUG ? ' · bug fix' : ''}
            </span>
          </span>
        </button>
      ))}
    </section>
  ));
}

// Same rule as the Friday popup (release note present, no Process items), grouped into weeks that
// start on Friday so each group is what that week's popup showed.
function WhatsNew({ items, onOpen }) {
  const groups = useMemo(() => {
    const done = items
      .filter((s) => s.status === 'Done' && s.releaseNote?.trim() && s.category !== 'Process')
      .sort((a, b) => deliveredOn(b) - deliveredOn(a));
    const byWeek = new Map();
    done.forEach((s) => {
      const d = new Date(deliveredOn(s));
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - ((d.getDay() + 2) % 7));
      const key = start.getTime();
      byWeek.set(key, [...(byWeek.get(key) || []), s]);
    });
    return [...byWeek.entries()];
  }, [items]);

  if (!groups.length) return <div className="sg-empty">Nothing delivered yet.</div>;
  return groups.map(([start, rows]) => (
    <section key={start} className="sg-month">
      <h2>Week of {fmtDate(start)}</h2>
      {rows.map((s) => (
        <button type="button" key={s.id} className="sg-log" onClick={() => onOpen(s.id)}>
          <span className="sg-log-date">{fmtDate(deliveredOn(s))}</span>
          <span className="sg-log-main">
            <strong>{s.releaseNote}</strong>
            {s.area && <span className="sg-faint">{s.area}</span>}
          </span>
        </button>
      ))}
    </section>
  ));
}

function Stats({ items }) {
  const stats = useMemo(() => {
    const open = items.filter((s) => OPEN_STATUSES.includes(s.status));
    const now = new Date();
    const delivered = items.filter((s) => s.status === 'Done' && s.deliveredAt);
    const thisMonth = delivered.filter((s) => { const d = new Date(s.deliveredAt); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
    const days = delivered.map((s) => (+new Date(s.deliveredAt) - +new Date(s.createdAt)) / DAY).filter((d) => d >= 0).sort((a, b) => a - b);
    const mid = Math.floor(days.length / 2);
    const median = days.length ? (days.length % 2 ? days[mid] : (days[mid - 1] + days[mid]) / 2) : null;
    return [
      { label: 'Open', value: open.length },
      { label: 'Open bugs', value: open.filter((s) => s.category === BUG).length },
      { label: 'In progress', value: items.filter((s) => s.status === 'In progress').length },
      { label: 'Delivered this month', value: thisMonth.length },
      { label: 'Median days to deliver', value: median === null ? '—' : Math.round(median) },
    ];
  }, [items]);
  return (
    <div className="sg-stats">
      {stats.map((x) => <div key={x.label} className="sg-stat"><div className="sg-stat-value">{x.value}</div><div className="sg-stat-label">{x.label}</div></div>)}
    </div>
  );
}

// Buckets in the fixed AREAS order, unassigned last, so a group's position never depends on the data.
function groupRows(rows) {
  const groups = AREAS.map((a) => [a, rows.filter((s) => s.area === a)]);
  groups.push(['No area yet', rows.filter((s) => !s.area || !AREAS.includes(s.area))]);
  return groups.filter(([, g]) => g.length);
}

const TABS = [['roadmap', 'Roadmap'], ['list', 'List'], ['changelog', 'Changelog'], ['whatsnew', "What's New"]];

// Everyone sees every suggestion and can reply, vote and follow; edit/triage rules are enforced by
// the API (suggestions.service.ts) and mirrored here only to decide which buttons to show.
function SuggestionReviews() {
  const { currentUser } = useApp();
  const isAdmin = currentUser?.role === 'admin';
  const me = useMemo(() => ({ id: currentUser?.id, name: currentUser?.name }), [currentUser]);
  const [params, setParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // `?tab=whatsnew` is how the Friday popup's "Go to backlog" lands on the What's New list.
  const [tab, setTab] = useState(TABS.some(([k]) => k === params.get('tab')) ? params.get('tab') : 'roadmap');
  const [openId, setOpenId] = useState(params.get('id'));

  // Default view hides closed work (Done / Declined / Duplicate) -- the open set is pre-ticked.
  const [statuses, setStatuses] = useState(OPEN_STATUSES);
  const [categories, setCategories] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [groupByArea, setGroupByArea] = useState(false);
  const [mine, setMine] = useState(false);
  const [text, setText] = useState('');
  const [sort, setSort] = useState('newest');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setItems(await apiFetch('/suggestions'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isAdmin) apiFetch('/users').then(setUsers).catch(() => {});
  }, [isAdmin]);

  // Open items the model hasn't looked at (submitted before triage existed, or its call failed).
  const untriaged = items.filter((s) => OPEN_STATUSES.includes(s.status) && (!s.aiSummary || !s.area)).length;
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState('');
  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { queued } = await apiFetch('/suggestions/triage-backfill', { method: 'POST' });
      setBackfillMsg(`Triage is running on ${queued} suggestion${queued === 1 ? '' : 's'} — reload in a minute to see the results.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBackfilling(false);
    }
  };

  const replaceItem = (updated) => setItems((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
  const ctx = useMemo(
    () => ({ me, isAdmin, users, items, reload: load, open: setOpenId }),
    [me, isAdmin, users, items, load],
  );

  const closeDetail = () => {
    setOpenId(null);
    if (params.get('id')) setParams({}, { replace: true });
  };

  // Everything except status: the roadmap and changelog are organized by status already.
  const base = useMemo(() => {
    const q = text.trim().toLowerCase();
    return items.filter((s) => {
      if (categories.length && !categories.includes(s.category)) return false;
      if (priorities.length && !priorities.includes(s.priority)) return false;
      if (areas.length && !areas.includes(s.area)) return false;
      if (mine && s.submittedBy?.id !== me.id) return false;
      if (q && !s.text.toLowerCase().includes(q) && !(s.submittedBy?.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, categories, priorities, areas, mine, text, me.id]);

  const rows = useMemo(
    () => base.filter((s) => !statuses.length || statuses.includes(s.status)).sort(SORTS[sort].fn),
    [base, statuses, sort],
  );

  const isDefault = statuses.length === OPEN_STATUSES.length && OPEN_STATUSES.every((x) => statuses.includes(x))
    && !categories.length && !priorities.length && !areas.length && !mine && !text.trim();
  const reset = () => { setStatuses(OPEN_STATUSES); setCategories([]); setPriorities([]); setAreas([]); setMine(false); setText(''); };
  const detail = items.find((x) => x.id === openId);

  return (
    <div className="sg-page">
      <div className="page-header">
        <div className="page-eyebrow">Feedback</div>
        <h1 className="page-title">Suggestions &amp; Roadmap</h1>
        <p className="page-subtitle">
          Ideas, bugs and requests from everyone — what is being looked at, what is planned, what is being
          built and what has shipped. Vote on what matters to you, reply, and follow what you care about.
        </p>
      </div>

      <ErrorBanner error={error} />
      {isAdmin && untriaged > 0 && (
        <div className="notice notice-info" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>
            {backfillMsg || `${untriaged} open suggestion${untriaged === 1 ? ' has' : 's have'} no AI triage yet.`}
          </span>
          {!backfillMsg && (
            <button type="button" className="btn btn-outline btn-sm" disabled={backfilling} onClick={runBackfill}>
              {backfilling ? 'Starting…' : 'Run AI triage on them'}
            </button>
          )}
        </div>
      )}
      <Stats items={items} />

      <div className="sg-tabs" role="tablist">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? 'is-active' : ''} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      <div className="card sg-filters">
        {tab === 'list' && <MultiSelect label="Status" options={opt(STATUSES)} selected={statuses} onChange={setStatuses} allLabel="All statuses" showSelectAll />}
        <MultiSelect label="Category" options={opt(CATEGORIES)} selected={categories} onChange={setCategories} allLabel="All categories" />
        <MultiSelect label="Area" options={opt(AREAS)} selected={areas} onChange={setAreas} allLabel="All areas" minWidth={190} />
        <MultiSelect label="Priority" options={opt(PRIORITIES)} selected={priorities} onChange={setPriorities} allLabel="Any priority" minWidth={140} />
        <SearchBox value={text} onChange={setText} placeholder="Suggestion text or submitter" />
        <FilterCheckbox label="My suggestions" checked={mine} onChange={setMine} />
        {tab === 'list' && <FilterCheckbox label="Group by area" checked={groupByArea} onChange={setGroupByArea} />}
        {tab === 'list' && (
          <div className="form-group" style={{ marginBottom: 0, minWidth: 150 }}>
            <label className="form-label" htmlFor="sg-sort">Sort</label>
            <select id="sg-sort" className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
              {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        )}
        {!isDefault && <button type="button" className="btn btn-outline" onClick={reset}>Reset filters</button>}
        <div className="sg-count">
          {loading ? 'Loading…' : tab === 'list' ? `${rows.length} of ${items.length}` : `${base.length} of ${items.length}`}
        </div>
      </div>

      {tab === 'list' && (
        <div className="sg-list">
          {!loading && rows.length === 0 && (
            <div className="sg-empty">{items.length === 0 ? 'No suggestions yet.' : 'Nothing matches these filters.'}</div>
          )}
          {(groupByArea ? groupRows(rows) : [[null, rows]]).map(([area, group]) => (
            <React.Fragment key={area || 'all'}>
              {area && <h2 className="sg-area-head">{area} <span className="sg-count-pill">{group.length}</span></h2>}
              {group.map((s) => <SuggestionCard key={s.id} s={s} ctx={ctx} onChange={replaceItem} />)}
            </React.Fragment>
          ))}
        </div>
      )}

      {tab === 'roadmap' && (
        <div className="sg-board">
          {BOARD.map((col) => {
            const colRows = base.filter(col.match).sort(col.key === 'done' ? (a, b) => deliveredOn(b) - deliveredOn(a) : SORTS.priority.fn);
            return (
              <section key={col.key} className="sg-col" aria-label={col.title}>
                <header><h2>{col.title}</h2><span className="sg-count-pill">{colRows.length}</span></header>
                <div className="sg-faint">{col.hint}</div>
                {colRows.map((s) => <MiniCard key={s.id} s={s} onOpen={setOpenId} />)}
                {!loading && colRows.length === 0 && <div className="sg-faint sg-col-empty">Nothing here</div>}
              </section>
            );
          })}
        </div>
      )}

      {tab === 'changelog' && <Changelog items={base} onOpen={setOpenId} />}
      {tab === 'whatsnew' && <WhatsNew items={base} onOpen={setOpenId} />}

      {detail && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Suggestion detail" onClick={closeDetail}>
          <div className="modal" style={{ maxWidth: 760 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Suggestion</span>
              <button type="button" className="modal-close" onClick={closeDetail} aria-label="Close">×</button>
            </div>
            <div className="modal-body">
              <SuggestionCard s={detail} ctx={ctx} onChange={replaceItem} defaultOpen />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuggestionReviews;
